import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import {
  APPConfig,
  convertToBoolean,
  IS_BROWSER,
  getLocalStorage,
} from 'config/appConfig'
import loadable from '@loadable/component'
import {
  checkIsMobile,
  getLocaleCodeFromUrl,
  getLocalePathParam,
  i18nTranslate,
  trackMysiteAddCartEvent,
  getImageFromProperty,
} from 'src/utils'
import { NUSKIN_ACCOUNT_DATA } from 'src/utils/constant'
import {
  isEquinoxMysite,
  isMysiteHomePage,
  isEquinoxMysiteAdminPreview,
  getProductPriceForTracking,
  findDefaultSku,
} from 'src/utils/mySiteUtils'
import {
  getMySiteDefaultCategoriesFromCS,
  getDefaultCategoriesFromCSMap,
} from 'src/utils/contentStackUtils'
import ProductStatusBadge from '../CatalogViews/ProductCard/ProductStatusBadge'
import { pageNames } from 'src/routes/pathParams'
import { catalogDeps, searchDeps, productDeps } from 'src/deps'
import { cartContainer, catalogContainer, mysiteContainer } from 'src/models'
import { ProductCardPrice } from '../Price'
import MetaDecorator from '../MetaDecorator'
import { productStatusDeps } from 'src/views/components/ProductComponents/V2ProductQuantityAndButtons/ProductStatusDeps'
import { imagePlaceholder } from 'src/static/fixtures/imagePlaceholder'
import './styles.scss'
import { toastState } from 'src/views/components'
import { customerContainer } from 'src/models'
import { Button } from 'react-bootstrap'
import { ADD_CART_GTM, GTM_METHOD_ADD, VIEW_PDP } from 'src/utils/constant'

const ProductTabCarousalComponent = loadable(
  () =>
    import('@nuskin/react-mysite-elements').then(module => {
      if (module?.ProductTabCarousel) {
        return module.ProductTabCarousel
      }
      console.error(
        'ProductTabCarousel component not found in @nuskin/react-mysite-elements'
      )
      return () => null
    }),
  {
    fallback: <div />,
    ssr: false,
  }
)

function getLangLocaleStore() {
  const locale = getLocaleCodeFromUrl({ defaultLocale: 'en_US' }) || null
  const lang = locale?.split('_')[0] || 'en'
  const storeId = APPConfig?.getAppConfig()?.storeId || null
  return { lang, locale, storeId }
}

function getCategoriesNameAndSkuId(lang) {
  const shopHeader = mysiteContainer?.mysiteShopHeaderResponse || null
  const categoriesInfo = shopHeader?.market?.lang?.[lang]?.categoriesInfo || []
  const marketCategories = shopHeader?.market?.categories || []

  const categoriesInfoMap = {}
  categoriesInfo.forEach(category => {
    if (category?.categoryId) {
      categoriesInfoMap[category.categoryId] = category
    }
  })

  return marketCategories.reduce((acc, category) => {
    if (!category) return acc

    const id = category.id ?? null
    const skuIds = Array.isArray(category.skus)
      ? category.skus.map(skuItem => skuItem?.sku ?? null).filter(Boolean)
      : []
    if (id !== undefined && id !== null) {
      const categoryInfo = categoriesInfoMap[id] || {}

      const defaultCategoryInfo =
        mysiteContainer?.mysiteDefaultCategoryInfo || {}
      const name = categoryInfo.categoryName || defaultCategoryInfo.categoryName

      const description =
        categoryInfo.description || defaultCategoryInfo.description

      acc[id] = {
        id,
        name,
        description,
        skuIds,
        categoryId: id,
        categoryName: name,
        ...categoryInfo,
      }
    }

    return acc
  }, {})
}

const ProductTabCarousalContainer = () => {
  const { lang } = getLangLocaleStore() || { lang: null }
  const [mysiteShopHeaderResponse, setMysiteShopHeaderResponse] = useState(
    mysiteContainer?.mysiteShopHeaderResponse || null
  )
  const showCustomCategory =
    mysiteShopHeaderResponse?.market?.showCustomCategory || false
  const showDefaultCategories =
    mysiteShopHeaderResponse?.market?.showDefaultCategories || false
  const [categoriesById, setCategoriesById] = useState({})
  const [categoriesArr, setCategoriesArr] = useState([])
  const [isMysiteHP, setIsMysiteHP] = useState(isMysiteHomePage() ?? false)
  const isGroupSkuEnabled = convertToBoolean(
    APPConfig?.getAppConfig()?.enableMysiteGroupSkuIdApi
  )

  useEffect(() => {
    const currentResponse = mysiteContainer?.mysiteShopHeaderResponse
    setMysiteShopHeaderResponse(currentResponse)

    if (isEquinoxMysite() && showCustomCategory) {
      ProductTabCarousalComponent.preload()
    }
  }, [])

  /* Set up a periodic check for async data updates*/
  useEffect(() => {
    const interval = setInterval(() => {
      const currentResponse = mysiteContainer?.mysiteShopHeaderResponse
      if (currentResponse && currentResponse !== mysiteShopHeaderResponse) {
        setMysiteShopHeaderResponse(currentResponse)
      }
    }, 500)
    return () => clearInterval(interval)
  }, [mysiteShopHeaderResponse])

  useEffect(() => {
    if (!(mysiteShopHeaderResponse || lang)) return
    let isCancelled = false

    const buildCategories = async () => {
      if (showDefaultCategories) {
        const csCategories =
          mysiteContainer?.mysiteDefaultCategoriesFromCS || []
        if (csCategories.length === 0) {
          await getMySiteDefaultCategoriesFromCS()
        }
        if (isCancelled) return
      }

      const newCategoriesById = showDefaultCategories
        ? getDefaultCategoriesFromCSMap()
        : getCategoriesNameAndSkuId(lang) || {}
      const newCategoriesArr = Object.values(newCategoriesById) || []
      const newCategoriesByIdWithSkuIds = newCategoriesArr.filter(
        category => category.skuIds && category.skuIds.length > 0
      )
      setCategoriesById(newCategoriesById)
      setCategoriesArr(newCategoriesByIdWithSkuIds)
    }

    buildCategories()
    return () => {
      isCancelled = true
    }
  }, [lang, mysiteShopHeaderResponse, showDefaultCategories])

  /* mysite category section only to be displayed in homepage not other page */
  useEffect(() => {
    const handleLocationChange = () => {
      setIsMysiteHP(isMysiteHomePage())
    }
    window.addEventListener('popstate', handleLocationChange)
    const originalPushState = window.history.pushState
    const originalReplaceState = window.history.replaceState

    window.history.pushState = function (...args) {
      originalPushState.apply(window.history, args)
      handleLocationChange()
    }

    window.history.replaceState = function (...args) {
      originalReplaceState.apply(window.history, args)
      handleLocationChange()
    }
    return () => {
      window.removeEventListener('popstate', handleLocationChange)
      window.history.pushState = originalPushState
      window.history.replaceState = originalReplaceState
    }
  }, [])
  const history = useHistory()

  useEffect(() => {
    const appWrapperElement = document.getElementById('id-app-wrapper')
    if (appWrapperElement) {
      if (isMysiteHP) {
        appWrapperElement.classList.remove('app-wrapper')
      } else {
        appWrapperElement.classList.add('app-wrapper')
      }
    }
    /* Cleanup function to restore the class when component unmounts*/
    return () => {
      if (
        appWrapperElement &&
        !appWrapperElement.classList.contains('app-wrapper')
      ) {
        appWrapperElement.classList.add('app-wrapper')
      }
    }
  }, [isMysiteHP])

  const fetchCategories = async () => {
    return categoriesArr.map(category => ({
      label: category?.name ?? category?.categoryName ?? '',
      categoryId: category?.id ?? category?.categoryId ?? null,
    }))
  }

  const getCategoryId = categoryId => {
    mysiteContainer.selectedCategoryId = categoryId
  }

  const handleAddtoCart = async (skuInfo = {}) => {
    if (customerContainer.isRegisterUser) {
      const localeFromUrl = (
        getLocaleCodeFromUrl()?.split('_')?.[1] || 'US'
      )?.toUpperCase()
      const userHomeMarket = customerContainer
        .getUserHomeCountry()
        ?.toUpperCase()
      const notEligibleForAddToCart =
        customerContainer.restrictCrossMarketAccess(
          userHomeMarket,
          localeFromUrl
        )
      if (notEligibleForAddToCart) {
        toastState.setToastMessage(
          i18nTranslate(
            'cart.homeMarket',
            'Please switch to your home market to make a purchase.'
          )
        )
        return
      }
    }

    if (!skuInfo) return null

    let showToast = false
    if (checkIsMobile()) {
      showToast = true
    }
    const isAddedToCartSuccessfully = await searchDeps.handleAddtoCart(
      skuInfo,
      false,
      showToast
    )
    /** Only open the mini cart / track the event when the add-to-cart call actually succeeded */
    if (!isAddedToCartSuccessfully) return
    if (!cartContainer.isNotifyDirectSellerPopupOpen) {
      cartContainer.toggleCartVisibility()
    }
    const productTrackingData = productTrackingObject(skuInfo)
    trackMysiteAddCartEvent({
      type: ADD_CART_GTM,
      product: productTrackingData,
      method: GTM_METHOD_ADD,
    })
  }
  function productTrackingObject(skuInfo) {
    if (!skuInfo) return null
    const sku = findDefaultSku({ sku: skuInfo?.sku })
    const productProperties =
      skuInfo?.skuProperties || skuInfo?.properties || {}
    const skuProperties = sku?.skuProperties || sku?.properties || {}
    const priceData = getProductPriceForTracking(skuInfo, skuInfo?.type)
    return {
      name: productProperties?.name || productProperties?.slug || '',
      id: skuInfo?.skuId || sku?.identifier || skuInfo?.identifier || '',
      category:
        skuInfo?.properties?.categoryId || productProperties?.categoryId || '',
      brand: productProperties?.brand || skuProperties?.brand || '',
      variant:
        skuProperties?.variantLabel ||
        productProperties?.variantLabel ||
        'Normal',

      price:
        priceData?.priceAfterDiscount === 0
          ? priceData?.originalPrice
          : (priceData?.priceAfterDiscount ??
            skuInfo?.totalValue?.priceAfterDiscount ??
            sku?.totalValue?.priceAfterDiscount ??
            ''),
      quantity: skuInfo?.quantity || 1,
      item_type: '',
      item_view: VIEW_PDP,
      method: 'cart',
      cartType: 'Standard',
    }
  }

  const mapProductToCard = (
    product,
    addToCartButtonLabel,
    isRegularProduct = true
  ) => {
    let key, productTitle, imageUrl, disabled, productStatusBadgeProps

    const enableDirectSellerButton =
      convertToBoolean(APPConfig?.getAppConfig()?.enableDirectSellerButton) &&
      (typeof productDeps?.isDirectSellerProduct === 'function'
        ? productDeps.isDirectSellerProduct(product)
        : false)
    const selectedCategoryId = mysiteContainer?.selectedCategoryId
    const selectedCategory = mysiteShopHeaderResponse?.market?.categories?.find(
      cat => cat?.id === selectedCategoryId
    )
    if (isRegularProduct) {
      const selectedProduct = {
        ...product,
        selectedSku: product?.sku?.[0],
      }
      const { disableOneTime = false, disableSubscription = false } =
        productStatusDeps.getV2IndividualProductInventoryStatus(selectedProduct)

      key = String(product?.sku?.[0]?.identifier ?? '')
      productTitle =
        product?.name ??
        product?.productName ??
        product?.sku?.[0]?.skuProperties?.name ??
        product?.sku?.[0]?.skuproperties?.name ??
        'No Name'
      const skuIdentifier = product?.sku?.[0]?.identifier
      const matchingSku = selectedCategory?.skus?.find(
        skuItem =>
          skuItem?.sku === skuIdentifier || skuItem?.globalId === skuIdentifier
      )
      imageUrl =
        matchingSku?.selectedImageUrl ||
        product?.imageURL ||
        product?.sku?.[0]?.skuProperties?.imageURL ||
        ''
      disabled = disableOneTime || disableSubscription
      productStatusBadgeProps = {
        product,
        selectedPlpSku: product?.sku,
        size: 'small',
      }
    } else {
      let disableButton = false
      if (isGroupSkuEnabled) {
        const {
          disableBundleKitOneTime = false,
          disableBundleKitSubscription = false,
        } = productStatusDeps.getBundleKitProductLevelInventoryStatus(product)
        disableButton = disableBundleKitOneTime || disableBundleKitSubscription
      } else {
        disableButton = shouldDisableProduct(product)
      }
      key = String(product?.identifier ?? '')
      productTitle = product?.properties?.name ?? 'No Name'
      const productIdentifier = product?.identifier
      const matchingSku = selectedCategory?.skus?.find(
        skuItem =>
          skuItem?.sku === productIdentifier ||
          skuItem?.productId === productIdentifier
      )
      imageUrl =
        matchingSku?.selectedImageUrl ||
        getImageFromProperty(product?.properties) ||
        ''
      disabled = disableButton
      productStatusBadgeProps = {
        product,
        size: 'small',
      }
    }

    return {
      key,
      productTitle,
      productStatusBadge: <ProductStatusBadge {...productStatusBadgeProps} />,
      directSeller: enableDirectSellerButton ? (
        <Button
          type="button"
          aria-label={i18nTranslate('direct-seller-pdp', 'Direct Seller')}
          variant="contained"
          className="direct-seller-plp-in-button"
          data-testid="qa-direct-seller-pdp">
          {i18nTranslate('direct-seller-pdp', 'Direct Seller')}
        </Button>
      ) : null,
      mySitePriceComponent: (
        <div className="mysite-homepage-product-price">
          <ProductCardPrice product={product} productType={product?.type} />
        </div>
      ),
      imageDetails: {
        imageSrc: !!imageUrl ? imageUrl : imagePlaceholder,
        imageAlt: product?.name ?? product?.productName ?? 'No Name',
        fallbackImage: imagePlaceholder,
      },
      onCardClick: () => {
        const pdpLink = getPdpLink(product)
        if (typeof pdpLink === 'string' && pdpLink.startsWith('/')) {
          history.push(pdpLink)
        }
      },
      button: {
        showButton: true,
        buttonText: addToCartButtonLabel,
        buttonVariant: 'contained',
        buttonColor: 'primary',
        startIcon: 'cart',
        disabled: disabled,
        onClick: () => {
          if (!disabled) {
            handleAddtoCart(product)
          }
        },
      },
    }
  }

  const getPdpLink = product => {
    if (!product) return ''
    const defaultSkuArr = catalogDeps?.getDefaultSelectedSku?.(product) ?? []
    const defaultSku =
      defaultSkuArr && defaultSkuArr.length > 0 ? defaultSkuArr[0] : undefined
    const productOrSlugId =
      product?.properties?.slug ?? product?.identifier ?? ''
    let link = `${pageNames?.product ?? ''}/${productOrSlugId}`
    const skuId = defaultSku?.identifier ?? ''
    const skuSlug = defaultSku?.properties?.slug ?? ''
    const hasMoreThanOneSku = (product?.sku?.length ?? 1) > 1
    const variantParameter = skuSlug || skuId || ''
    if (hasMoreThanOneSku && variantParameter) {
      link += `?variant=${variantParameter}`
    }
    return link
  }

  const shouldDisableProduct = blundleSkuOrKit => {
    let properties = {}
    properties = blundleSkuOrKit?.properties || {}
    return (
      (properties?.inventoryStatus &&
        properties.inventoryStatus.toLowerCase() !== 'in stock') ||
      (properties?.productStatus &&
        properties.productStatus.toLowerCase() !== 'sellable') ||
      (properties?.status && properties.status.toLowerCase() !== 'active') ||
      properties?.dangerousGoods === true
    )
  }

  const fetchProductsBySearch = async categoryId => {
    const category = categoriesById?.[categoryId] ?? null
    if (!category?.skuIds?.length) return []
    const skuIds = category.skuIds ?? []
    const size = skuIds.length

    // First call: index_key_skuId
    const filterSku = {
      filters: [
        {
          field: 'index_key_skuId',
          operation: 'IN',
          value: `[${skuIds.join(',')}]`,
        },
      ],
    }
    const dataSku = await catalogContainer?.getSearchProducts?.({
      queryParams: {},
      contextName: '',
      size,
      contextUrlPathFromSSR: '',
      contextQueryParamsFromSSR: '',
      filterValue: filterSku,
      isMysiteAdminPreview: isEquinoxMysiteAdminPreview(),
    })
    const productsSku = dataSku?.product || []

    // Find missing skuIds
    const foundSkuIds = new Set(
      productsSku.map(product =>
        product?.sku &&
        Array.isArray(product.sku) &&
        product.sku[0]?.identifier !== undefined
          ? String(product.sku[0]?.identifier ?? '')
          : String(product?.skuId ?? product?.identifier ?? '')
      )
    )
    const missingSkuIds = skuIds.filter(id => !foundSkuIds.has(String(id)))

    let productsProductId = []
    if (missingSkuIds.length > 0) {
      // Second call: index_key_productId for missing ids
      const filterProductId = {
        filters: [
          {
            field: 'index_key_productId',
            operation: 'IN',
            value: `[${missingSkuIds.join(',')}]`,
          },
        ],
      }
      // Use catalogContainer.getSearchProducts for this call
      const dataProductId = await catalogContainer?.getSearchProducts?.({
        queryParams: {},
        contextName: '',
        size: missingSkuIds.length,
        contextUrlPathFromSSR: '',
        contextQueryParamsFromSSR: '',
        filterValue: filterProductId,
        isMysiteAdminPreview: isEquinoxMysiteAdminPreview(),
      })
      productsProductId = dataProductId?.product || []
    }
    const addToCartButtonLabel = i18nTranslate(
      'wishlist_addtocart',
      'ADD TO CART'
    ) /* reusing existing translation key */

    // ...mapping logic remains unchanged...
    const mappedSkuProducts = productsSku.map(product => {
      return mapProductToCard(product, addToCartButtonLabel, true)
    })
    const mappedProductIdProducts = productsProductId.map(product => {
      return mapProductToCard(product, addToCartButtonLabel, false)
    })

    // Combine results and preserve original order
    const productMap = new Map()
    mappedSkuProducts.forEach(product =>
      productMap.set(String(product.key), product)
    )
    mappedProductIdProducts.forEach(product =>
      productMap.set(String(product.key), product)
    )
    /*filtering skus list from sponsor reponse with actual product search api response */
    const finalProducts = skuIds
      .map(id => productMap.get(String(id)))
      .filter(Boolean)

    return finalProducts
  }
  const fetchProductByGroupSkuId = async categoryId => {
    const category = categoriesById?.[categoryId] ?? null
    if (!category?.skuIds?.length) return []
    const skuIdsList = category.skuIds ?? []

    try {
      const filterValue = Array.isArray(skuIdsList)
        ? skuIdsList.join(',')
        : skuIdsList

      const isMysiteAdminPreview = isEquinoxMysiteAdminPreview()
      let customHeaders = {}

      if (isMysiteAdminPreview) {
        const authToken = getLocalStorage('accessToken') ?? ''

        const enableEidTokenForSearchAPI = convertToBoolean(
          APPConfig?.getAppConfig()?.enableEidTokenForSearchAPI
        )

        if (authToken && enableEidTokenForSearchAPI) {
          customHeaders['authorization'] = `Bearer ${authToken}`
          customHeaders['x-mysite-preview'] = 'true'
        }
      }

      const requestBody = {
        queryParams: {
          skipSearchContentful: true,
        },
        size: skuIdsList.length,
        filterValue: {
          filters: [
            {
              field: 'groupskuid',
              operation: 'IN',
              value: filterValue,
            },
          ],
        },
        customHeaders: customHeaders,
        isMysiteAdminPreview: isMysiteAdminPreview,
      }
      const response = await catalogContainer.getSearchProducts(requestBody)

      const addToCartButtonLabel = i18nTranslate(
        'wishlist_addtocart',
        'ADD TO CART'
      )
      const allProducts = response?.product?.map(product => {
        const isRegularProduct =
          product?.properties?.customProductType === 'Regular SKU'

        return mapProductToCard(product, addToCartButtonLabel, isRegularProduct)
      })

      const productMap = new Map()
      for (const product of allProducts) {
        productMap.set(String(product.key), product)
      }

      const finalProducts = skuIdsList
        .map(id => productMap.get(String(id)))
        .filter(Boolean)

      return finalProducts
    } catch (error) {
      console.error('Error fetching products by groupskuid:', error)
      return []
    }
  }

  const getViewAllLink = () => {
    return `/mysite-categories`
  }

  const productTabCarousalProps = {
    settings: {
      slidesToShow: showCustomCategory ? 4 : 5,
      slidesToScroll: 4,
      infinite: false,
      dots: true,
    },
    allEntryLabel: i18nTranslate('subscription_viewall', 'View All'),
    emptyProductTabs: i18nTranslate(
      'mysite.noTabsAvailable',
      'No tabs available'
    ),
    emptyProductMessage: i18nTranslate(
      'mysite.noProductAvailable',
      'No Products Available For The Selected Category'
    ),
    productsCountLabel: i18nTranslate('plp.matchesCount', 'Products'),
    allEntryLink: getViewAllLink(),
    showControlButtons: true,
    showCustomCategory: showCustomCategory,
    fetchCategories,
    fetchProducts: isGroupSkuEnabled
      ? fetchProductByGroupSkuId
      : fetchProductsBySearch,
    getCategoryId,
    history,
  }

  let hasPreviewParam = false
  if (IS_BROWSER) {
    const urlParams = new URLSearchParams(window.location.search)
    hasPreviewParam = urlParams.has('preview')
  }
  const pageTitle = hasPreviewParam
    ? i18nTranslate('mysite_preview_title', 'My Site Preview |  Nu Skin')
    : i18nTranslate('mysite_homepage_title', 'My Site Home |  Nu Skin')

  const pageDescription = hasPreviewParam
    ? i18nTranslate('mysite_preview_description', 'Nuskin My Site Preview Page')
    : i18nTranslate('mysite_homepage_description', 'Nuskin My Site Home Page')
  const isToShowMysiteHomepage =
    mysiteContainer?.mysiteShopHeaderResponse?.market
      ?.showHomePageMarketingContent || false
  return (
    isEquinoxMysite() &&
    isMysiteHP && (
      <>
        <MetaDecorator
          title={pageTitle}
          description={pageDescription}
          imageUrl=""
          imageAlt=""
          data-testid="qa-mysite-home-carousal"
        />
        {showCustomCategory && (
          <div
            className={`${isToShowMysiteHomepage ? 'product-tab-carousal-wrapper' : ''}`}>
            <ProductTabCarousalComponent {...productTabCarousalProps} />
          </div>
        )}
      </>
    )
  )
}

export default ProductTabCarousalContainer
